/**
 * Cookie consent for analytics.
 *
 * Nothing tracks until the visitor explicitly accepts. `initAnalytics()` bails
 * out unless the stored choice is 'accepted', so a first-time visitor and
 * anyone who declines loads no PostHog and no GTM at all (rather than
 * loading them and opting out afterwards).
 *
 * The helpers take the storage object so they stay pure and unit-testable, the
 * same way `gtmShouldLoad` takes a location. They also tolerate storage being
 * unavailable: Safari in private mode throws on `localStorage` access, and the
 * right answer there is "no consent recorded" rather than a crash.
 */

export type ConsentChoice = 'accepted' | 'declined'

export const CONSENT_STORAGE_KEY = 'lw-cookie-consent'

/** localStorage when it is reachable, else null (SSR, private mode, blocked). */
export function defaultStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

/** The visitor's stored choice, or null when they have not chosen yet. */
export function readConsent(storage: Storage | null = defaultStorage()): ConsentChoice | null {
  try {
    const value = storage?.getItem(CONSENT_STORAGE_KEY)
    return value === 'accepted' || value === 'declined' ? value : null
  } catch {
    return null
  }
}

/** Persist the visitor's choice. */
export function writeConsent(
  choice: ConsentChoice,
  storage: Storage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(CONSENT_STORAGE_KEY, choice)
  } catch {
    // Storage unavailable: the banner simply asks again on the next visit.
  }
}

/** Analytics may only load once the visitor has explicitly accepted. */
export function analyticsAllowed(choice: ConsentChoice | null): boolean {
  return choice === 'accepted'
}
